import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Building2,
  Calendar,
  Hash,
  User,
  Tag,
  FileText,
  Paperclip,
  Save,
  Loader2,
  X,
  Plus,
  Image,
  ExternalLink,
  Upload,
  Download,
  Sparkles,
  CheckCircle2,
  Link2,
  Search,
  ArrowLeftRight,
  Flag,
} from "lucide-react";

const scoreTone = (score) => {
  if (score >= 75) return { bar: "bg-emerald-500", text: "text-emerald-700", label: "Strong match" };
  if (score >= 50) return { bar: "bg-amber-500", text: "text-amber-700", label: "Possible match" };
  if (score > 0) return { bar: "bg-slate-400", text: "text-slate-500", label: "Weak match" };
  return { bar: "bg-slate-300", text: "text-slate-400", label: "No signal" };
};

// Explainable "why it matches" panel: a score bar plus, on demand, the
// per-signal breakdown (amount, merchant, date, currency, reference) with the
// points each earned and a plain reason, so a user can see exactly why a
// document was suggested for a transaction.
const MatchBreakdown = ({ score, breakdown }) => {
  const [open, setOpen] = useState(false);
  const tone = scoreTone(score);
  const signals = breakdown?.signals || [];
  const adjustments = breakdown?.adjustments || [];
  return (
    <div className="mt-1.5">
      <div className="flex items-center gap-2">
        <span className={`text-xs font-semibold ${tone.text}`}>
          {score}% · {tone.label}
        </span>
        {breakdown && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2"
            data-testid="match-why-toggle"
          >
            {open ? "Hide why" : "Why?"}
          </button>
        )}
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full ${tone.bar} rounded-full transition-all`}
          style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
        />
      </div>
      {open && breakdown && (
        <div className="mt-2 space-y-1">
          {signals.map((s) => (
            <div key={s.key} className="flex items-start gap-2 text-xs">
              <span
                className={`mt-1 inline-flex h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                  s.hit ? "bg-emerald-500" : "bg-slate-300"
                }`}
              />
              <span className="w-16 flex-shrink-0 text-slate-500">{s.label}</span>
              <span className="w-10 flex-shrink-0 font-medium text-slate-700">
                {s.points}/{s.max}
              </span>
              <span className={s.hit ? "text-slate-600" : "text-slate-400"}>{s.reason}</span>
            </div>
          ))}
          {adjustments.map((a, i) => {
            const penalty = a.factor && a.factor < 1;
            return (
              <div
                key={i}
                className={`flex items-start gap-2 text-xs ${
                  penalty ? "text-rose-600" : "text-emerald-600"
                }`}
              >
                <span
                  className={`mt-1 inline-flex h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                    penalty ? "bg-rose-400" : "bg-emerald-500"
                  }`}
                />
                <span>{a.reason}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Radix refuses an empty string as a SelectItem value, so "nobody" needs a
// stand-in. It never leaves this file: handleSave turns it back into the null
// the API reads as a clear.
const UNASSIGNED = "none";

const TransactionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isManager } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Suggested attachments state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [attachingSuggestion, setAttachingSuggestion] = useState(null);
  
  // File search state
  const [fileSearch, setFileSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingFiles, setSearchingFiles] = useState(false);
  const [showFileSearch, setShowFileSearch] = useState(false);
  const searchDebounceRef = useRef(null);

  // Form state
  const [categoryId, setCategoryId] = useState("");
  const [statusId, setStatusId] = useState("");
  const [assignedUserId, setAssignedUserId] = useState(UNASSIGNED);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [transRes, statusRes, catRes, filesRes] = await Promise.all([
          apiService.getTransaction(id),
          apiService.getStatuses(),
          apiService.getCategories(),
          apiService.getFiles(),
        ]);

        const trans = transRes.data;
        setTransaction(trans);
        setStatuses(statusRes.data);
        setCategories(catRes.data);
        setFiles(filesRes.data);

        // Set form values
        setCategoryId(trans.category_id || "");
        setStatusId(trans.status_id || "");
        setAssignedUserId(trans.assigned_user_id || UNASSIGNED);
        setNotes(trans.notes || "");

        if (isManager) {
          const usersRes = await apiService.getUsers();
          setUsers(usersRes.data);
        }
      } catch (error) {
        console.error("Failed to fetch transaction:", error);
        toast.error("Failed to load transaction");
        navigate("/transactions");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, isManager]);

  // Auto-fetch suggestions when transaction loads
  useEffect(() => {
    if (transaction?.id) {
      fetchSuggestions();
    }
  }, [transaction?.id]);

  const fetchSuggestions = async () => {
    if (!id) return;
    setLoadingSuggestions(true);
    try {
      const res = await apiService.getTransactionSuggestions(id);
      // Filter out already attached files
      const attachedIds = transaction?.attachments?.map(a => a.file_id) || [];
      const filteredSuggestions = (res.data.suggestions || []).filter(
        s => !attachedIds.includes(s.file_id)
      );
      setSuggestions(filteredSuggestions);
    } catch (error) {
      console.error("Failed to fetch suggestions:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAttachSuggestion = async (suggestion) => {
    setAttachingSuggestion(suggestion.file_id);
    try {
      await apiService.attachFile(id, suggestion.file_id);
      toast.success(`Attached "${suggestion.filename}"`);
      // Refresh transaction to get updated attachments
      const transRes = await apiService.getTransaction(id);
      setTransaction(transRes.data);
      // Remove from suggestions
      setSuggestions(prev => prev.filter(s => s.file_id !== suggestion.file_id));
      // Remove from search results if present
      setSearchResults(prev => prev.filter(f => f.id !== suggestion.file_id));
    } catch (error) {
      toast.error("Failed to attach file");
    } finally {
      setAttachingSuggestion(null);
    }
  };

  // Debounced typeahead: as the user types, ask the backend for inbox files
  // that match the query text AND rank them by how well they score against this
  // transaction, so the best candidate (with its "why") surfaces first.
  const handleFileSearch = (query) => {
    setFileSearch(query);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearchingFiles(false);
      return;
    }
    setSearchingFiles(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await apiService.searchTransactionFiles(id, query.trim());
        setSearchResults(res.data.results || []);
      } catch (error) {
        console.error("Failed to search files:", error);
        setSearchResults([]);
      } finally {
        setSearchingFiles(false);
      }
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleAttachSearchResult = async (result) => {
    setAttachingSuggestion(result.file_id);
    try {
      await apiService.attachFile(id, result.file_id);
      toast.success(`Attached "${result.filename}"`);

      // Refresh transaction to get updated attachments
      const transRes = await apiService.getTransaction(id);
      setTransaction(transRes.data);

      // Remove from search results
      setSearchResults(prev => prev.filter(f => f.file_id !== result.file_id));

      // Clear search
      setFileSearch("");
      setShowFileSearch(false);
    } catch (error) {
      toast.error("Failed to attach file");
    } finally {
      setAttachingSuggestion(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {};
      if (categoryId !== transaction.category_id)
        data.category_id = categoryId || null;
      if (statusId !== transaction.status_id) data.status_id = statusId || null;
      // UNASSIGNED is the select's stand-in for "nobody"; Radix will not take an
      // empty string as an item value. Translating it back here is what stops
      // the literal "none" being stored as a user id.
      const assignee = assignedUserId === UNASSIGNED ? null : assignedUserId || null;
      if (assignee !== (transaction.assigned_user_id || null))
        data.assigned_user_id = assignee;
      if (notes !== transaction.notes) data.notes = notes;

      if (Object.keys(data).length > 0) {
        await apiService.updateTransaction(id, data);
        toast.success("Transaction updated");

        // Refresh
        const res = await apiService.getTransaction(id);
        setTransaction(res.data);
      } else {
        toast.info("No changes to save");
      }
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // First upload the file
      const uploadRes = await apiService.uploadFile(file, transaction.counterparty_name || "receipt");
      const fileId = uploadRes.data.id;
      
      // Then attach it to the transaction
      await apiService.attachFile(id, fileId);
      
      toast.success("File uploaded and attached");
      
      // Refresh transaction
      const res = await apiService.getTransaction(id);
      setTransaction(res.data);
    } catch (error) {
      const message = error.response?.data?.detail || "Upload failed";
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAttachFile = async (fileId) => {
    try {
      await apiService.attachFile(id, fileId);
      toast.success("File attached");

      // Refresh transaction
      const res = await apiService.getTransaction(id);
      setTransaction(res.data);
    } catch (error) {
      toast.error("Failed to attach file");
    }
  };

  const handleDetachFile = async (fileId) => {
    try {
      await apiService.detachFile(id, fileId);
      toast.success("File detached");

      // Refresh transaction
      const res = await apiService.getTransaction(id);
      setTransaction(res.data);
    } catch (error) {
      toast.error("Failed to detach file");
    }
  };

  const formatAmount = (amount, direction) => {
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return direction === "income" ? `+${formatted}` : `-${formatted}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!transaction) {
    return null;
  }

  const status = statuses.find((s) => s.id === transaction.status_id);
  const category = categories.find((c) => c.id === transaction.category_id);
  const assignedUser = users.find((u) => u.id === transaction.assigned_user_id);

  const unattachedFiles = files.filter(
    (f) => !transaction.attachments?.some((a) => a.id === f.id)
  );

  return (
    <div className="space-y-6" data-testid="transaction-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/transactions")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">
            Transaction Details
          </h1>
          <p className="text-sm text-slate-500">
            {transaction.bank} • {transaction.currency}
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="save-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount Card */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <div
              className={`p-6 ${
                transaction.direction === "income"
                  ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                  : "bg-gradient-to-r from-red-500 to-rose-500"
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  {transaction.direction === "income" ? (
                    <TrendingUp className="w-7 h-7 text-white" />
                  ) : (
                    <TrendingDown className="w-7 h-7 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-white/80 text-sm font-medium">
                    {transaction.direction === "income" ? "Income" : "Expense"}
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {formatAmount(transaction.amount, transaction.direction)}{" "}
                    {transaction.currency}
                  </p>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="font-medium text-slate-900">
                      {formatDate(transaction.transaction_date)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Bank / Account</p>
                    <p className="font-medium text-slate-900">
                      {transaction.bank}
                    </p>
                    <p className="text-xs text-slate-500 font-mono">
                      {transaction.account_id}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Hash className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Reference</p>
                    <p className="font-mono text-sm text-slate-900">
                      {transaction.reference || "—"}
                    </p>
                  </div>
                </div>
                {transaction.counterparty_name && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Counterparty</p>
                      <p className="font-medium text-slate-900">
                        {transaction.counterparty_name}
                      </p>
                      {transaction.counterparty_iban && (
                        <p className="text-xs text-slate-500 font-mono">
                          {transaction.counterparty_iban}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {transaction.description && (
                <>
                  <Separator className="my-6" />
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Description</p>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">
                      {transaction.description}
                    </p>
                  </div>
                </>
              )}

              {transaction.fee_amount > 0 && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Fee:</strong> {transaction.fee_amount}{" "}
                    {transaction.currency}
                  </p>
                </div>
              )}

              {transaction.fx_rate && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>FX Rate:</strong> {transaction.fx_rate}
                  </p>
                </div>
              )}

              {/* Status indicators */}
              <div className="mt-4 space-y-2">
                {transaction.reconciled && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <ArrowLeftRight className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-blue-700 font-medium">This transaction has been reconciled</span>
                  </div>
                )}
                
                {transaction.is_flagged && (
                  <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <Flag className="w-4 h-4 text-orange-500" />
                    <span className="text-sm text-orange-700 font-medium">This transaction is flagged for review</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Attachments
              </CardTitle>
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-attachment-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-1" />
                  )}
                  Upload
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Current Attachments */}
              {transaction.attachments?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                  {transaction.attachments.map((file) => (
                    <div
                      key={file.id}
                      className="relative group border rounded-lg p-3 hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Image className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">
                            {file.filename}
                          </p>
                          <p className="text-xs text-slate-500">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-100 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDetachFile(file.id)}
                        data-testid={`detach-file-${file.id}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Suggested Attachments & Search */}
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-purple-900 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {loadingSuggestions ? 'Finding Matches...' : suggestions.length > 0 ? `Suggested (${suggestions.length})` : 'Find Documents'}
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-purple-700 hover:text-purple-900 hover:bg-purple-100"
                    onClick={() => setShowFileSearch(!showFileSearch)}
                  >
                    <Search className="w-3 h-3 mr-1" />
                    Search
                  </Button>
                </div>
                
                {/* File Search Box */}
                {showFileSearch && (
                  <div className="space-y-2 mb-3">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <Input
                        value={fileSearch}
                        onChange={(e) => handleFileSearch(e.target.value)}
                        placeholder="Search inbox..."
                        className="h-8 pl-8 text-sm bg-white"
                        data-testid="file-search-input"
                      />
                      {searchingFiles && (
                        <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-slate-400" />
                      )}
                    </div>
                    
                    {/* Search Results - ranked by match score, with the "why" */}
                    {searchResults.length > 0 && (
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {searchResults.map((result) => (
                          <div
                            key={result.file_id}
                            className="flex items-start gap-2 p-2 bg-white rounded-lg border hover:border-purple-300 transition-colors"
                          >
                            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-4 h-4 text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-700 truncate">{result.filename}</p>
                              {result.document_info?.merchant_name && (
                                <p className="text-xs text-slate-400 truncate">{result.document_info.merchant_name}</p>
                              )}
                              <MatchBreakdown score={result.score} breakdown={result.breakdown} />
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-purple-200 text-purple-700 hover:bg-purple-100 flex-shrink-0"
                              onClick={() => handleAttachSearchResult(result)}
                              disabled={attachingSuggestion === result.file_id}
                            >
                              {attachingSuggestion === result.file_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Link2 className="w-3 h-3 mr-1" />
                                  Attach
                                </>
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Search empty state */}
                    {fileSearch.trim().length >= 2 && !searchingFiles && searchResults.length === 0 && (
                      <p className="text-xs text-slate-500 text-center py-2">No files found matching "{fileSearch}"</p>
                    )}
                  </div>
                )}

                {loadingSuggestions ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="space-y-2">
                    {suggestions.map((suggestion) => (
                      <div
                        key={suggestion.file_id}
                        className="flex items-start justify-between p-3 bg-white rounded-lg border border-purple-100"
                      >
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {suggestion.filename}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                              {suggestion.document_info?.merchant_name && (
                                <span>{suggestion.document_info.merchant_name}</span>
                              )}
                              {suggestion.document_info?.amount && (
                                <span className="font-medium">
                                  {suggestion.document_info.currency || '€'}{suggestion.document_info.amount}
                                </span>
                              )}
                              {suggestion.document_info?.date && (
                                <span>{suggestion.document_info.date}</span>
                              )}
                            </div>
                            <MatchBreakdown score={suggestion.score} breakdown={suggestion.breakdown} />
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 flex-shrink-0 ml-2"
                          onClick={() => handleAttachSuggestion(suggestion)}
                          disabled={attachingSuggestion === suggestion.file_id}
                        >
                          {attachingSuggestion === suggestion.file_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Link2 className="w-4 h-4 mr-1" />
                              Attach
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-purple-600 text-center py-2">
                    No suggestions found. Use search to find documents manually.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Edit Form */}
        <div className="space-y-6">
          {/* Status & Category */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Classification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={statusId} onValueChange={setStatusId}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: s.color }}
                          />
                          {s.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories
                      .filter((c) => c.active)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {isManager && users.length > 0 && (
                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <Select
                    value={assignedUserId}
                    onValueChange={setAssignedUserId}
                  >
                    <SelectTrigger data-testid="assigned-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {users
                        .filter((u) => u.active)
                        .map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} ({u.role})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="Add notes about this transaction..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                data-testid="notes-textarea"
              />
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  {transaction.notes_updated_at && (
                    <span>
                      Last saved by <strong>{transaction.notes_updated_by_name || "Unknown"}</strong>
                      {" · "}
                      {new Date(transaction.notes_updated_at).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
                      })}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant={notes !== transaction.notes ? "default" : "outline"}
                  disabled={saving || notes === transaction.notes}
                  onClick={handleSave}
                  className={notes !== transaction.notes ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  data-testid="save-notes-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Save Notes
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Current Status Display */}
          <Card className="border-0 shadow-sm bg-slate-50">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">
                Current Status
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Status</span>
                  {status ? (
                    <Badge
                      style={{
                        backgroundColor: `${status.color}20`,
                        color: status.color,
                      }}
                    >
                      {status.name}
                    </Badge>
                  ) : (
                    <span className="text-sm text-slate-400">Not set</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Category</span>
                  {category ? (
                    <Badge variant="outline">{category.name}</Badge>
                  ) : (
                    <span className="text-sm text-slate-400">Not set</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Assigned To</span>
                  {assignedUser ? (
                    <span className="text-sm font-medium text-slate-900">
                      {assignedUser.name}
                    </span>
                  ) : (
                    <span className="text-sm text-slate-400">Unassigned</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailPage;
